import { Component, OnInit, inject } from '@angular/core';
import { AuthService } from './auth/services/auth.service';

import { AppAbility, defineAbilitiesFor } from 'src/app/shared/services/AppAbility';
import { Platform } from '@angular/cdk/platform';
import { Router } from '@angular/router';
import { merge } from 'rxjs';
import { icons } from 'src/assets/images/icons';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { PhotoDraftService } from './core/services/photoDraftService/photo-draft.service';
import { PhotoDraftQuotaError } from './core/services/photoDraftService/photo-draft.model';
import { ImageService } from './core/services/imageService/image.service';
import { SyncEngine } from './core/offline/sync-engine.service';
import { OfflineAvailabilityService } from './core/offline/offline-availability.service';
import { OfflineStore } from './core/offline/offline-store.service';
import { LegacyQueueMigration } from './core/offline/legacy-queue-migration.service';
import { OfflineDeviceDataService } from './core/offline/offline-device-data.service';
import { AppUpdateService } from './core/services/appUpdateService/app-update.service';
import { UnreadPollerService } from './notifications/services/unread-poller.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
  title = 'bavox';

  showOnlineAlert: boolean = false;
  initialized: boolean = false;
  isOnline!: boolean;
  modalVersion!: boolean;
  modalPwaEvent: any;
  modalPwaPlatform: string|undefined;
  installApp!: boolean;
  platformPrompt: boolean = false;

  public actionIcon: string = icons.actionIcon;
  public addShortcutIcon: string = icons.addShortcutIcon;

  /** «Jetzt aktualisieren» was tapped; the update service waits for the sync, then reloads */
  updatingVersion = false;
  private readonly legacyMigration = inject(LegacyQueueMigration);
  private readonly deviceData = inject(OfflineDeviceDataService);
  private readonly appUpdate = inject(AppUpdateService);
  private readonly unreadPoller = inject(UnreadPollerService);

  constructor(
    private authService: AuthService,
    private platform: Platform,
    private readonly ability: AppAbility,
    private photoDraftService: PhotoDraftService,
    private imageService: ImageService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private router: Router,
    private syncEngine: SyncEngine,
    private offlineAvailability: OfflineAvailabilityService,
    private offlineStore: OfflineStore,
  ) {
    this.isOnline = false;
    this.modalVersion = false;
  }
  
  ngOnInit() {
    void this.photoDraftService.cleanup(30);

    // Offline E2 step 3: the old task queue of this user moves into the outbox once (plan D10), then the sync runs
    void this.legacyMigration
      .run()
      .then((result) => (result.migrated > 0 ? this.syncEngine.run() : undefined))
      .catch(() => undefined);
    // Offline E2: Outbox-Sync (Trigger: Start, online, sichtbar, alle 60 s) und Offline-Schnappschüsse
    this.syncEngine.start();
    this.offlineAvailability.start();
    // Gate E-2 b: an app start with a session counts as use of this device; data of users absent for 30 days expires
    const userId = this.authService.getUserId();
    if (userId) void this.deviceData.markActiveAndExpire(userId);
    // a tablet where the app stays open for weeks is in use too (Rev1 review H4)
    this.deviceData.watchActivity();
    // Speicher voll beim Schnappschuss oder beim Erfassen (Spec 6): ein Hinweis mit Link «Offline-Speicher» (Review H5)
    merge(this.offlineAvailability.quota$, this.offlineStore.quota$).subscribe(() => {
      this.toastr
        .error(this.translate.instant('offline.quota'), '', { positionClass: 'toast-top-center' })
        .onTap.subscribe(() => void this.router.navigate(['/profile', 'settings', 'offline-storage']));
    });
    // Offline E2 step 4: saved plans that left the device on a refresh (deleted, replaced, no access; plan E5)
    this.offlineAvailability.documentsRemoved$.subscribe((removed) => {
      const names = removed.map((event) => `«${event.name}»`).join(', ');
      this.toastr
        .info(this.translate.instant('offline.documents-removed', { count: removed.length, names }), '', { positionClass: 'toast-top-center' })
        .onTap.subscribe(() => void this.router.navigate(['/profile', 'settings', 'offline-storage']));
    });

    this.imageService.storageErrors$.subscribe((error) => {
      const key = error instanceof PhotoDraftQuotaError ? 'photo-drafts.quota' : 'photo-drafts.storage-error';
      this.toastr.error(this.translate.instant(key), '', { positionClass: 'toast-top-center' });
    });

    this.checkRole();
    
    this.updateOnlineStatus();
    window.addEventListener('online',  this.updateOnlineStatus.bind(this));
    window.addEventListener('offline', this.updateOnlineStatus.bind(this));
    
    // F1: new app version → version modal; checks at start, when visible, every 30 min (only with the SW on)
    this.appUpdate.updateReady$.subscribe((ready) => (this.modalVersion = ready));
    this.appUpdate.updating$.subscribe((updating) => (this.updatingVersion = updating));
    this.appUpdate.start();
    // Benachrichtigungen: Glocken-Zahl sofort, alle 60 s, bei sichtbar und nach Navigation abfragen
    this.unreadPoller.start();

    this.loadModalPwa();
  }

  private updateOnlineStatus(): void {
    const wasOnline = this.isOnline;
    this.isOnline = navigator.onLine;
    if (this.isOnline && !wasOnline && this.initialized) {
      this.showOnlineAlert = true;
      setTimeout(() => this.showOnlineAlert = false, 4000);
    }
    if (!this.initialized) {
      this.initialized = true;
    }
  }

  public updateVersion(): void {
    void this.appUpdate.activate();
  }

  public closeVersion(): void {
    this.appUpdate.dismiss();
  }

  private loadModalPwa(): void {
    this.platformPrompt = true;
    this.installApp = !!localStorage.getItem('installApp');
    if (this.platform.ANDROID) {
      window.addEventListener('beforeinstallprompt', (event: any) => {
        event.preventDefault();
        this.modalPwaEvent = event;
        this.modalPwaPlatform = 'ANDROID';
      });
    }

    if (this.platform.IOS && this.platform.SAFARI) {
      const isInStandaloneMode = ('standalone' in window.navigator) && ((<any>window.navigator)['standalone']);
      if (!isInStandaloneMode) {
        this.modalPwaPlatform = 'IOS';
      }
    }
  }

  public addToHomeScreen(): void {
    this.modalPwaEvent.prompt();
    this.modalPwaPlatform = undefined;
  }

  public closePwa(): void {
    localStorage.setItem('installApp', 'true');
    this.platformPrompt = false;
    // this.modalPwaPlatform = undefined;
  }

  private checkRole(){
    const role = this.authService.getUserRole();
    this.ability.update(defineAbilitiesFor(role));
  }

}
